/**
 * FloorPlanEditorFabric — Fabric.js-powered floor plan canvas.
 * The sole floor plan editor as of the Fabric.js migration (the old
 * hand-rolled canvas implementation has been removed; see git history for
 * FloorPlanEditor.tsx / FloorPlanEditor.css if it's ever needed for reference).
 *
 * Features:
 *  - Built-in undo/redo (Ctrl+Z / Ctrl+Y) via command stack
 *  - Native multi-select (Shift+click, drag-select)
 *  - Snap-to-grid (10 px), mouse-wheel zoom
 *  - Serialisation via canvas.toJSON(); SVG export with embedded metadata
 *  - Object handles for resize/rotate/move; locked zones disable them
 *  - Inline rename (double-click), contextual selection toolbar
 *  - Fill badges, named seats on head tables, group colour + pulse highlight
 *  - Mobile pinch-zoom via fabric's built-in touch events
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Canvas, Circle, Ellipse, FabricImage, FabricObject, FabricText, Pattern, Point, Rect, util } from 'fabric';
import { FloorPlan, Room, VenueData } from '../types/Planner';
import { DecorItem, DecorPlacement } from '../types/Decor';
import { loadDecorItems, refreshDecorFromApi } from '../services/DecorService';
import { listDecorPlacements, createDecorPlacement, updateDecorPlacementPosition, deleteDecorPlacement } from '../services/DecorPlacementService';
import { useNotifications } from './NotificationProvider';
import { parseSvgMetadata, exportFabricFloorPlanSvg, downloadSvgFloorPlan, tableShapeFromSvg } from '../services/FloorPlanSvgExport';
import { uploadImage, resolveMediaUrl } from '../services/uploadService';
import Icon from './Icon';
import SearchableSelect from './ui/SearchableSelect';
import { ROOM_COLORS, computeChairPositions, roomColor, sectionLetterOf, snap } from './floor-plan-editor/floorPlanGeometry';
import { SEAT_LIMITS, clampSeats, defaultTableSize, nextTableNumber, rectangularLength, rotateQuarterTurn } from './floor-plan-editor/tableGeometry';
import type { TableShape } from './floor-plan-editor/tableGeometry';
import { ELEMENT_PRESETS, defaultElementName } from './floor-plan-editor/elementPresets';
import type { ElementPreset } from './floor-plan-editor/elementPresets';
import { buildWhiteWeddingHall } from './floor-plan-editor/whiteWeddingHall';
import { cascadePosition } from './floor-plan-editor/layoutContext';
import SeatCountInput from './floor-plan-editor/SeatCountInput';
import {
    ANCHOR_TOP_LEFT,
    addChairsToCanvas,
    addDecorationsToCanvas,
    addRoomToCanvas,
    applyLockState,
    facingFor,
    labelStyleFor,
    loadBackgroundImage,
    pinBackgroundToBack,
    reassertBodiesBelowShapes,
    reassertChairsAboveTables,
    reassertDecorationsAboveTables,
    reassertLabelsOnTop,
    refreshBodiesForRoom,
    removeBodiesForRoom,
    removeChairsForRoom,
    removeDecorationsForRoom,
    shapeFillFor,
} from './floor-plan-editor/fabricCanvasHelpers';
import '../styles/components/FloorPlanEditorFabric.css';

const FLOOR_BG_COLOR = '#f8f5f0';
const GRID_TILE_COLOR = '#eceae6';
const GRID_DOT_COLOR = 'rgba(0,0,0,0.12)';

// Grid is painted as a fabric background pattern, not CSS on the wrapping
// div — fabric's own <canvas> is opaque and fully covers any CSS background
// on its container, so a CSS-only grid (the pre-Fabric-migration approach)
// is invisible no matter what. Also note fabric's canvas.clear() resets
// backgroundColor/backgroundImage to defaults, so this must be re-applied
// after every clear(), not just once on mount.
function buildGridPattern(): Pattern {
    const tile = document.createElement('canvas');
    tile.width = 20;
    tile.height = 20;
    const ctx = tile.getContext('2d')!;
    ctx.fillStyle = GRID_TILE_COLOR;
    ctx.fillRect(0, 0, 20, 20);
    ctx.fillStyle = GRID_DOT_COLOR;
    ctx.beginPath();
    ctx.arc(0.5, 0.5, 1, 0, Math.PI * 2);
    ctx.fill();
    return new Pattern({ source: tile as unknown as HTMLImageElement, repeat: 'repeat' });
}

interface FloorPlanEditorFabricProps {
    venue: VenueData;
    floorPlan: FloorPlan;
    onFloorPlanUpdate: (fp: FloorPlan) => void;
    onRoomAdd: (room: Omit<Room, 'id'>) => void;
    onRoomUpdate: (roomId: string, updates: Partial<Room>) => void;
    onRoomRemove: (roomId: string) => void;
    onRoomSelect?: (roomId: string | null) => void;
    embedded?: boolean;
    externalSelectedRoomId?: string | null;
    tableStatusMap?: Record<string, 'empty' | 'partial' | 'full'>;
    guestGroupCounts?: Record<string, number>;
    onGroupDrop?: (groupId: string, roomId: string) => void;
}

export const FloorPlanEditorFabric: React.FC<FloorPlanEditorFabricProps> = ({
    venue,
    floorPlan,
    onFloorPlanUpdate,
    onRoomAdd,
    onRoomUpdate,
    onRoomRemove,
    onRoomSelect,
    externalSelectedRoomId,
    tableStatusMap = {},
    guestGroupCounts = {},
    onGroupDrop,
}) => {
    const canvasElRef = useRef<HTMLCanvasElement>(null);
    const fabricRef   = useRef<Canvas | null>(null);
    const historyRef  = useRef<string[]>([]);
    const redoRef     = useRef<string[]>([]);
    const fileInputRef   = useRef<HTMLInputElement>(null);
    const importInputRef = useRef<HTMLInputElement>(null);
    const { showSuccess, showError } = useNotifications();
    const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);

    // floorPlan mirrored into a ref so the persistent fabric event handlers
    // registered once in the init effect (empty deps) always read the latest
    // rooms instead of a stale closure over the floorPlan from first render.
    const floorPlanRef = useRef(floorPlan);
    useEffect(() => { floorPlanRef.current = floorPlan; }, [floorPlan]);

    // ── Inline rename (migration gap 6/11) ────────────────────────────────────
    const [renamingRoomId, setRenamingRoomId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const renameInputRef = useRef<HTMLInputElement>(null);

    // Mirrors removeRoom (declared later, defined below) so the same persistent
    // keydown handler always calls the latest version rather than a stale one
    // captured on first render.
    const removeRoomRef = useRef<(roomId: string) => void>(() => {});
    const addTableRef = useRef<(shape: TableShape) => void>(() => {});
    const handleZoomFitRef = useRef<() => void>(() => {});
    const pendingFitRef = useRef(false);
    const localIdCounter = useRef(0);

    // ── Scale calibration ────────────────────────────────────────────────────
    // Refs mirror the state below so the persistent fabric 'mouse:down' handler
    // (registered once in the init effect) always reads current values instead
    // of a stale closure over state from when the effect first ran.
    const [calibrating, setCalibratingState] = useState(false);
    const [calibPt1, setCalibPt1State] = useState<{ x: number; y: number } | null>(null);
    const [calibPt2, setCalibPt2] = useState<{ x: number; y: number } | null>(null);
    const [calibDialogOpen, setCalibDialogOpen] = useState(false);
    const [calibDistance, setCalibDistance] = useState('');
    const calibratingRef = useRef(false);
    const calibPt1Ref = useRef<{ x: number; y: number } | null>(null);
    const setCalibrating = useCallback((v: boolean) => { calibratingRef.current = v; setCalibratingState(v); }, []);
    const setCalibPt1 = useCallback((v: { x: number; y: number } | null) => { calibPt1Ref.current = v; setCalibPt1State(v); }, []);

    // ── Top toolbar: zoom / grid / zoom-lock ──────────────────────────────────
    // zoom is mirrored into state purely to re-render the "% " label — the
    // mouse-wheel handler (registered once in the init effect) reads/writes
    // fabric's own zoom directly and only needs to notify React afterwards.
    const [zoomPct, setZoomPct] = useState(100);
    const [showGrid, setShowGrid] = useState(() => floorPlan.showGrid ?? false);
    const [zoomLocked, setZoomLockedState] = useState(false);
    const zoomLockedRef = useRef(false);
    const setZoomLocked = useCallback((v: boolean) => { zoomLockedRef.current = v; setZoomLockedState(v); }, []);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    // Panning (space+drag or middle-mouse-drag) — refs since pan state
    // itself needn't trigger renders, but viewportTick does so the
    // selection toolbar's screen position (derived from the live fabric
    // viewportTransform) recomputes as the canvas is dragged around.
    const isPanningRef = useRef(false);
    const spacePressedRef = useRef(false);
    const lastPanPointRef = useRef<{ x: number; y: number } | null>(null);
    const [viewportTick, setViewportTick] = useState(0);
    const bumpViewport = useCallback(() => setViewportTick(t => t + 1), []);
    const [fpAddOpen, setFpAddOpen] = useState(false);
    const [fpZoneType, setFpZoneType] = useState('other');
    const fpAddRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (fpAddRef.current && !fpAddRef.current.contains(e.target as Node)) setFpAddOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Decor placements: a decor item positioned on this floor plan. One
    // item can have multiple placements up to its quantity. Rendered as a
    // separate marker layer alongside rooms/chairs. ──
    const [decorItems, setDecorItems] = useState<DecorItem[]>(loadDecorItems());
    const [decorPlacements, setDecorPlacements] = useState<DecorPlacement[]>([]);
    const [decorPickerOpen, setDecorPickerOpen] = useState(false);
    const [selectedDecorPlacementId, setSelectedDecorPlacementId] = useState<number | null>(null);
    const decorPickerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (decorPickerRef.current && !decorPickerRef.current.contains(e.target as Node)) setDecorPickerOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    useEffect(() => { refreshDecorFromApi().then(setDecorItems); }, []);
    useEffect(() => {
        const fpId = Number(floorPlan.id);
        if (!fpId) return;
        listDecorPlacements(fpId).then(setDecorPlacements).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [floorPlan.id]);

    // ── Initialise Fabric canvas ──────────────────────────────────────────────
    useEffect(() => {
        if (!canvasElRef.current) return;

        // Capture the real outer container BEFORE Canvas() reparents the
        // <canvas> into its own .canvas-container wrapper — after that,
        // canvasElRef.current.parentElement points at fabric's tiny wrapper
        // (sized to match the canvas itself), not the actual flex container,
        // which makes the resize observer below a no-op and leaves the canvas
        // stuck at fabric's 300x150 default.
        const outerContainer = canvasElRef.current.parentElement;

        const fc = new Canvas(canvasElRef.current, {
            selection: true,
            preserveObjectStacking: true,
            backgroundColor: FLOOR_BG_COLOR,
        });
        fabricRef.current = fc;

        // Chairs are separate objects (not grouped with their table), so a
        // table drag/resize must explicitly re-drive chair positions live —
        // otherwise chairs stay put until object:modified fires and the
        // room-sync effect rebuilds them, which reads as visibly broken
        // (chairs detached from the table) while the drag is in progress.
        const liveFollowChairs = (target: FabricObject) => {
            const t = target as FabricObject & { roomId?: string; roomType?: string; tableShapeType?: Room['tableShape']; seatCount?: number };
            if (!t.roomId) return;
            const isTableObj = t.roomType === 'table';
            if (!isTableObj) {
                const stored = floorPlanRef.current.rooms.find(r => r.id === t.roomId);
                if (stored) {
                    const w = (t.width ?? 0) * (t.scaleX ?? 1);
                    const h = (t.height ?? 0) * (t.scaleY ?? 1);
                    refreshBodiesForRoom(fc, { ...stored, position: { x: t.left ?? 0, y: t.top ?? 0 }, width: w, height: h }, floorPlanRef.current.rooms);
                }
                return;
            }
            const w = (t.width ?? 0) * (t.scaleX ?? 1);
            const h = (t.height ?? 0) * (t.scaleY ?? 1);
            const syntheticRoom: Room = {
                id: t.roomId, name: '', type: 'table',
                position: { x: t.left ?? 0, y: t.top ?? 0 },
                width: w, height: h,
                tableShape: t.tableShapeType, seats: t.seatCount,
            };
            const liveRooms = floorPlanRef.current.rooms;
            const chairPositions = computeChairPositions(syntheticRoom, facingFor(syntheticRoom, liveRooms));
            const chairs = fc.getObjects().filter(o => (o as FabricObject & { chairForRoomId?: string }).chairForRoomId === t.roomId);
            chairs.forEach((chair, i) => {
                const pos = chairPositions[i];
                if (!pos) return;
                chair.set({ left: pos.left, top: pos.top, angle: (pos.angleRad * 180) / Math.PI });
            });
            // crescent arc / stage tiers / route line follow the box while it is dragged or resized
            const stored = liveRooms.find(r => r.id === t.roomId);
            if (stored) refreshBodiesForRoom(fc, { ...stored, position: syntheticRoom.position, width: w, height: h }, liveRooms);
        };

        // Snap-to-grid on move/scale
        fc.on('object:moving', (ev) => {
            const target = ev.target;
            if (!target) return;
            target.set({ left: snap(target.left ?? 0), top: snap(target.top ?? 0) });
            liveFollowChairs(target);
        });
        fc.on('object:scaling', (ev) => {
            const target = ev.target;
            if (!target) return;
            const w = snap((target.width ?? 0) * (target.scaleX ?? 1));
            const h = snap((target.height ?? 0) * (target.scaleY ?? 1));
            target.set({ scaleX: 1, scaleY: 1, width: w, height: h });
            liveFollowChairs(target);
        });

        // Save state for undo after any modification
        fc.on('object:modified', (ev) => {
            const target = ev.target as (FabricObject & { decorPlacementId?: number }) | undefined;
            if (target?.decorPlacementId !== undefined) {
                updateDecorPlacementPositionRef.current(target.decorPlacementId, target.left ?? 0, target.top ?? 0);
                return;
            }
            historyRef.current.push(JSON.stringify(fc.toObject(['roomId', 'roomType'])));
            redoRef.current = [];
            setCanUndo(true);
            setCanRedo(false);
            syncFromCanvas(fc);
        });

        fc.on('selection:created', (ev) => {
            const first = (ev as { selected?: FabricObject[] }).selected?.[0] as (FabricObject & { roomId?: string; decorPlacementId?: number }) | undefined;
            if (first?.decorPlacementId !== undefined) {
                setSelectedDecorPlacementId(first.decorPlacementId);
                onRoomSelect?.(null);
                setSelectedRoomId(null);
                return;
            }
            setSelectedDecorPlacementId(null);
            onRoomSelect?.(first?.roomId ?? null);
            setSelectedRoomId(first?.roomId ?? null);
        });
        fc.on('selection:updated', (ev) => {
            const first = (ev as { selected?: FabricObject[] }).selected?.[0] as (FabricObject & { roomId?: string; decorPlacementId?: number }) | undefined;
            if (first?.decorPlacementId !== undefined) {
                setSelectedDecorPlacementId(first.decorPlacementId);
                onRoomSelect?.(null);
                setSelectedRoomId(null);
                return;
            }
            setSelectedDecorPlacementId(null);
            onRoomSelect?.(first?.roomId ?? null);
            setSelectedRoomId(first?.roomId ?? null);
        });
        fc.on('selection:cleared', () => {
            onRoomSelect?.(null);
            setSelectedRoomId(null);
            setSelectedDecorPlacementId(null);
        });

        // Keyboard shortcuts
        const onKey = (e: KeyboardEvent) => {
            const fc = fabricRef.current;
            if (!fc) return;
            // Polish pass: don't hijack Delete/Backspace/Ctrl+Z while the user is
            // typing in the rename input or any other text field on the page
            // (e.g. the sidebar's table-edit form) — those keys should edit text,
            // not delete the selected table or rewind canvas history.
            const activeEl = document.activeElement;
            const isTypingInField = activeEl instanceof HTMLInputElement || activeEl instanceof HTMLTextAreaElement;
            if (isTypingInField) return;
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault();
                handleUndoRef.current();
            }
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
                e.preventDefault();
                handleRedoRef.current();
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                const active = fc.getActiveObject();
                const id = (active as (FabricObject & { roomId?: string }) | undefined)?.roomId;
                if (id) removeRoomRef.current(id);
            }
            // Space held → hand-tool panning, mirroring Figma/design-tool
            // convention. Guard against key-repeat re-triggering the cursor
            // swap on every event while the key stays down.
            if (e.code === 'Space' && !spacePressedRef.current) {
                e.preventDefault();
                spacePressedRef.current = true;
                fc.defaultCursor = 'grab';
                fc.hoverCursor = 'grab';
            }
        };
        const onKeyUp = (e: KeyboardEvent) => {
            const fc = fabricRef.current;
            if (!fc) return;
            if (e.code === 'Space') {
                spacePressedRef.current = false;
                if (!isPanningRef.current) {
                    fc.defaultCursor = 'default';
                    fc.hoverCursor = 'move';
                }
            }
        };
        window.addEventListener('keydown', onKey);
        window.addEventListener('keyup', onKeyUp);

        // Resize observer — sized against the real outer container, not fabric's wrapper
        const ro = new ResizeObserver(() => {
            if (outerContainer) {
                fc.setDimensions({ width: outerContainer.clientWidth, height: outerContainer.clientHeight });
                fc.renderAll();
            }
        });
        if (outerContainer) {
            ro.observe(outerContainer);
            fc.setDimensions({ width: outerContainer.clientWidth, height: outerContainer.clientHeight });
        }

        // Custom events
        const onUpload  = () => fileInputRef.current?.click();
        const onImport  = () => importInputRef.current?.click();
        const onAddRoom = () => {
            const id = `room-${Date.now()}`;
            const rect = new Rect({
                ...ANCHOR_TOP_LEFT,
                left: 40, top: 40, width: 200, height: 120,
                fill: ROOM_COLORS.other, stroke: 'rgba(255,255,255,0.8)', strokeWidth: 2,
                rx: 6, ry: 6,
            }) as Rect & { roomId: string; roomType: string };
            rect.roomId = id;
            rect.roomType = 'other';
            fc.add(rect);
            const pos = { x: 40, y: 40 };
            onRoomAdd({ name: 'Zone', type: 'other', position: pos, width: 200, height: 120 });
        };
        // Routed through addTableRef (below) so it uses the same placement,
        // numbering and unique ids as the new table-shape menu items.
        const onAddTable = () => addTableRef.current('round');

        // Scale calibration: toggle mode (disables object selection while active
        // so clicking to place a calibration point doesn't drag a zone instead),
        // then capture two canvas-space points on click.
        const onCalibrate = () => {
            const next = !calibratingRef.current;
            setCalibrating(next);
            setCalibPt1(null);
            setCalibPt2(null);
            fc.discardActiveObject();
            fc.selection = !next;
            fc.forEachObject(o => { o.selectable = !next; o.evented = !next; });
            fc.renderAll();
        };
        fc.on('mouse:down', (opt) => {
            if (!calibratingRef.current) return;
            const pointer = fc.getScenePoint(opt.e);
            if (!calibPt1Ref.current) {
                setCalibPt1({ x: pointer.x, y: pointer.y });
                return;
            }
            setCalibPt2({ x: pointer.x, y: pointer.y });
            setCalibDialogOpen(true);
        });

        // ── Inline rename (migration gap 6/11): double-click a table/zone shape
        // or its label to rename it in place, matching the old editor's UX. ──
        fc.on('mouse:dblclick', (opt) => {
            if (calibratingRef.current) return;
            const target = opt.target as (FabricObject & { roomId?: string; labelForRoomId?: string }) | undefined;
            const roomId = target?.roomId ?? target?.labelForRoomId;
            if (!roomId) return;
            const room = floorPlanRef.current.rooms.find(r => r.id === roomId);
            if (!room || room.locked) return;
            setRenamingRoomId(roomId);
            setRenameValue(room.name);
        });

        // ── Mouse-wheel zoom (polish pass): parity with the old editor's
        // zoom controls, clamped to the same 0.25–2.5 range. ──
        fc.on('mouse:wheel', (opt) => {
            opt.e.preventDefault();
            opt.e.stopPropagation();
            if (zoomLockedRef.current) return;
            const delta = opt.e.deltaY;
            let newZoom = fc.getZoom() * (0.999 ** delta);
            newZoom = Math.min(2.5, Math.max(0.25, newZoom));
            fc.zoomToPoint(new Point(opt.e.offsetX, opt.e.offsetY), newZoom);
            setZoomPct(Math.round(newZoom * 100));
        });

        // ── Canvas panning: middle-mouse-drag, or space+left-drag (design-tool
        // convention). Uses raw clientX/clientY deltas rather than fabric's
        // canvas-space pointer, since relativePan wants screen-space movement
        // independent of the current zoom/viewport transform. ──
        fc.on('mouse:down', (opt) => {
            const e = opt.e as MouseEvent;
            if (calibratingRef.current) return;
            if (e.button !== 1 && !spacePressedRef.current) return;
            isPanningRef.current = true;
            lastPanPointRef.current = { x: e.clientX, y: e.clientY };
            fc.discardActiveObject();
            fc.selection = false;
            fc.defaultCursor = 'grabbing';
            fc.hoverCursor = 'grabbing';
            fc.renderAll();
            e.preventDefault();
        });
        fc.on('mouse:move', (opt) => {
            if (!isPanningRef.current || !lastPanPointRef.current) return;
            const e = opt.e as MouseEvent;
            const dx = e.clientX - lastPanPointRef.current.x;
            const dy = e.clientY - lastPanPointRef.current.y;
            fc.relativePan(new Point(dx, dy));
            lastPanPointRef.current = { x: e.clientX, y: e.clientY };
            bumpViewport();
        });
        fc.on('mouse:up', () => {
            if (!isPanningRef.current) return;
            isPanningRef.current = false;
            lastPanPointRef.current = null;
            fc.selection = true;
            fc.defaultCursor = spacePressedRef.current ? 'grab' : 'default';
            fc.hoverCursor = spacePressedRef.current ? 'grab' : 'move';
            fc.renderAll();
        });

        // ── Escape to deselect (polish pass) ──
        const onEscKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                fabricRef.current?.discardActiveObject();
                fabricRef.current?.renderAll();
            }
        };
        window.addEventListener('keydown', onEscKey);

        // ── Group colour pulse highlight (migration gap 4/11) ──
        // Dispatched by VenueManager's Groups summary panel when a group row
        // is clicked. Draws a temporary orange halo around every table that
        // belongs to the group and pulses its opacity a few times.
        const pulseObject = (obj: FabricObject) => {
            const bounds = obj.getBoundingRect();
            const halo = new Rect({
                ...ANCHOR_TOP_LEFT,
                left: bounds.left - 8, top: bounds.top - 8,
                width: bounds.width + 16, height: bounds.height + 16,
                fill: 'transparent', stroke: '#f97316', strokeWidth: 4,
                rx: 10, ry: 10, selectable: false, evented: false, opacity: 0,
            });
            fc.add(halo);
            fc.bringObjectToFront(halo);
            let pulses = 0;
            const fadeIn = () => halo.animate({ opacity: 1 }, {
                duration: 400, onChange: () => fc.renderAll(), onComplete: fadeOut,
            });
            function fadeOut() {
                halo.animate({ opacity: 0 }, {
                    duration: 400,
                    onChange: () => fc.renderAll(),
                    onComplete: () => {
                        pulses += 1;
                        if (pulses < 3) fadeIn();
                        else fc.remove(halo);
                    },
                });
            }
            fadeIn();
        };
        const onHighlightGroup = (e: Event) => {
            const groupId = (e as CustomEvent<string>).detail;
            if (!groupId) return;
            const fc2 = fabricRef.current;
            if (!fc2) return;
            const roomIds = new Set(floorPlanRef.current.rooms.filter(r => r.groupId === groupId).map(r => r.id));
            fc2.getObjects()
                .filter(o => (o as FabricObject & { roomId?: string }).roomId && roomIds.has((o as FabricObject & { roomId?: string }).roomId!))
                .forEach(o => pulseObject(o));
        };
        window.addEventListener('floor-plan-highlight-group', onHighlightGroup);

        // ── SVG export with Anojeje metadata (migration gap 3/11) ──
        // Fabric's own toSVG() serialises every shape as real vector markup —
        // no DOM-scraping hack needed (unlike the old editor's generateSvgWithMetadata).
        const onExportSvg = () => {
            const fp = floorPlanRef.current;
            const svg = exportFabricFloorPlanSvg(fc, {
                venueId: venue?.id,
                venueName: venue?.name,
                floorPlanName: fp.name,
                rooms: fp.rooms.map(r => ({
                    id: r.id, name: r.name, type: r.type, capacity: r.seats ?? r.capacity,
                    x: r.position.x, y: r.position.y, width: r.width, height: r.height,
                    tableShape: r.tableShape,
                    section: r.type === 'table' ? sectionLetterOf(r, fp.rooms) ?? undefined : undefined,
                })),
                dimensions: fp.dimensions,
                scale: fp.scale,
            });
            const fileName = `${(venue?.name ?? 'venue').replace(/\s+/g, '-')}_${fp.name.replace(/\s+/g, '-')}.svg`;
            downloadSvgFloorPlan(svg, fileName);
        };
        window.addEventListener('venue-export-png', onExportSvg);

        window.addEventListener('floor-plan-upload', onUpload);
        window.addEventListener('floor-plan-import', onImport);
        window.addEventListener('floor-plan-add-room', onAddRoom);
        window.addEventListener('floor-plan-add-table', onAddTable);
        window.addEventListener('floor-plan-calibrate', onCalibrate);

        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('keydown', onEscKey);
            window.removeEventListener('floor-plan-upload', onUpload);
            window.removeEventListener('floor-plan-import', onImport);
            window.removeEventListener('floor-plan-add-room', onAddRoom);
            window.removeEventListener('floor-plan-add-table', onAddTable);
            window.removeEventListener('floor-plan-calibrate', onCalibrate);
            window.removeEventListener('floor-plan-highlight-group', onHighlightGroup);
            window.removeEventListener('venue-export-png', onExportSvg);
            ro.disconnect();
            void fc.dispose();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Sync floorPlan → Fabric canvas ───────────────────────────────────────
    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc) return;

        fc.clear();
        applyCanvasBackground(fc, showGrid, !!(floorPlan.imageUrl ?? floorPlan.imageData));

        // Background image
        const bgUrl = floorPlan.imageUrl ?? floorPlan.imageData;
        if (bgUrl) {
            loadBackgroundImage(fc, bgUrl);
        }

        // Rooms — respect persisted layer order (sortOrder), fall back to array order
        const orderedRooms = [...floorPlan.rooms].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        for (const room of orderedRooms) {
            addRoomToCanvas(fc, room, guestGroupCounts, tableStatusMap, floorPlan.rooms);
        }

        renderDecorMarkers(fc);
        fc.renderAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [floorPlan.id]);

    // ── Sync background image without a full re-render ────────────────────────
    // The floorPlan.id-only effect above never re-runs when just imageUrl/
    // imageData changes (e.g. right after an upload finishes) — a full
    // fc.clear() there would also wipe every already-drawn room. So a
    // freshly uploaded background silently never appeared on canvas even
    // though the "Background saved" toast fired. This effect instead swaps
    // just the background image object in place.
    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc) return;
        const bgUrl = floorPlan.imageUrl ?? floorPlan.imageData;
        fc.getObjects()
            .filter(o => (o as FabricObject & { isBackgroundImage?: boolean }).isBackgroundImage)
            .forEach(o => fc.remove(o));
        applyCanvasBackground(fc, showGrid, !!bgUrl);
        if (bgUrl) {
            loadBackgroundImage(fc, bgUrl);
        } else {
            fc.renderAll();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [floorPlan.imageUrl, floorPlan.imageData]);

    // ── Decor markers: a separate fabric layer from rooms/chairs, driven by
    // decorPlacements rather than floorPlan.rooms. Each marker is a single
    // draggable FabricText (icon + item name) tagged decorPlacementId —
    // solid for procured items, muted/italic for planned/confirmed ones
    // still being sourced. ──
    const renderDecorMarkers = useCallback((fc: Canvas) => {
        fc.getObjects()
            .filter(o => (o as FabricObject & { decorPlacementId?: number }).decorPlacementId !== undefined)
            .forEach(o => fc.remove(o));
        for (const placement of decorPlacements) {
            const item = decorItems.find(d => Number(d.id) === placement.decor_id);
            if (!item) continue;
            const procured = item.status === 'procured' || item.status === 'installed';
            const marker = new FabricText(`✦ ${item.decor_element}`, {
                left: placement.x,
                top: placement.y,
                fontSize: 12,
                fontStyle: procured ? 'normal' : 'italic',
                fill: procured ? '#166534' : '#92400e',
                backgroundColor: procured ? 'rgba(34,197,94,0.16)' : 'rgba(217,119,6,0.14)',
                padding: 4,
                hasControls: false,
                lockScalingX: true,
                lockScalingY: true,
                lockRotation: true,
            }) as FabricText & { decorPlacementId: number };
            marker.decorPlacementId = placement.id;
            fc.add(marker);
        }
        fc.renderAll();
    }, [decorPlacements, decorItems]);

    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc) return;
        renderDecorMarkers(fc);
    }, [renderDecorMarkers]);

    // ── Sync individual room updates back to canvas ───────────────────────────
    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc) return;
        const rooms = floorPlan.rooms;

        // Drop canvas objects whose room no longer exists (an undo that removes a
        // room, or the placeholder shape older add handlers left behind).
        const liveIds = new Set(rooms.map(r => r.id));
        type Tagged = FabricObject & { roomId?: string; labelForRoomId?: string; chairForRoomId?: string; decorationForRoomId?: string; bodyForRoomId?: string };
        fc.getObjects().forEach(o => {
            const t = o as Tagged;
            const owner = t.roomId ?? t.labelForRoomId ?? t.chairForRoomId ?? t.decorationForRoomId ?? t.bodyForRoomId;
            if (owner !== undefined && !liveIds.has(owner)) fc.remove(o);
        });

        for (const room of rooms) {
            const obj = fc.getObjects().find(o => (o as FabricObject & { roomId?: string }).roomId === room.id);
            if (!obj) {
                addRoomToCanvas(fc, room, guestGroupCounts, tableStatusMap, rooms);
            } else {
                obj.set({
                    left: room.position.x,
                    top:  room.position.y,
                    fill: shapeFillFor(room),
                });
                applyLockState(obj, room.locked ?? false);
                if (obj instanceof Rect) {
                    obj.set({ width: room.width, height: room.height });
                } else if (obj instanceof Ellipse) {
                    obj.set({ rx: room.width / 2, ry: room.height / 2 });
                }
                obj.setCoords();
                if (room.type === 'table') {
                    (obj as FabricObject & { tableShapeType?: Room['tableShape']; seatCount?: number }).tableShapeType = room.tableShape;
                    (obj as FabricObject & { tableShapeType?: Room['tableShape']; seatCount?: number }).seatCount = room.seats ?? room.capacity;
                    removeChairsForRoom(fc, room.id);
                    addChairsToCanvas(fc, room, facingFor(room, rooms));
                }
                refreshBodiesForRoom(fc, room, rooms);
                // Label text isn't tracked by React state — keep it in sync so
                // inline rename (migration gap 6/11), sidebar edits and the
                // section letter (derived from position) all show up.
                const label = fc.getObjects().find(o => (o as FabricObject & { labelForRoomId?: string }).labelForRoomId === room.id) as FabricText | undefined;
                if (label) {
                    label.set(labelStyleFor(room, rooms));
                } else if (room.name) {
                    const newLabel = new FabricText(room.name, {
                        ...labelStyleFor(room, rooms),
                        selectable: false, evented: false,
                    }) as FabricText & { labelForRoomId?: string };
                    newLabel.labelForRoomId = room.id;
                    fc.add(newLabel);
                }
                removeDecorationsForRoom(fc, room.id);
                addDecorationsToCanvas(fc, room, guestGroupCounts, tableStatusMap, facingFor(room, rooms));
            }
        }
        reassertBodiesBelowShapes(fc);
        reassertChairsAboveTables(fc);
        reassertLabelsOnTop(fc);
        fc.renderAll();
        if (pendingFitRef.current) {
            pendingFitRef.current = false;
            handleZoomFitRef.current();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [floorPlan.rooms, tableStatusMap, guestGroupCounts]);

    const syncFromCanvas = useCallback((fc: Canvas) => {
        const rooms: Room[] = floorPlan.rooms.map(room => {
            const obj = fc.getObjects().find(o => (o as FabricObject & { roomId?: string }).roomId === room.id);
            if (!obj) return room;
            return {
                ...room,
                position: { x: Math.round(obj.left ?? room.position.x), y: Math.round(obj.top ?? room.position.y) },
                width:  Math.round((obj.width ?? room.width) * (obj.scaleX ?? 1)),
                height: Math.round((obj.height ?? room.height) * (obj.scaleY ?? 1)),
            };
        });
        onFloorPlanUpdate({ ...floorPlan, rooms });
    }, [floorPlan, onFloorPlanUpdate]);

    const applyCanvasBackground = useCallback((fc: Canvas, grid: boolean, hasImage: boolean) => {
        fc.backgroundColor = grid && !hasImage ? buildGridPattern() : FLOOR_BG_COLOR;
        fc.renderAll();
    }, []);

    const handleUndo = useCallback(() => {
        const fc = fabricRef.current;
        if (!fc || historyRef.current.length === 0) return;
        redoRef.current.push(JSON.stringify(fc.toObject(['roomId', 'roomType'])));
        const prev = historyRef.current.pop()!;
        void fc.loadFromJSON(JSON.parse(prev)).then(() => { fc.renderAll(); syncFromCanvas(fc); });
        setCanUndo(historyRef.current.length > 0);
        setCanRedo(true);
    }, [syncFromCanvas]);

    const handleRedo = useCallback(() => {
        const fc = fabricRef.current;
        if (!fc || redoRef.current.length === 0) return;
        historyRef.current.push(JSON.stringify(fc.toObject(['roomId', 'roomType'])));
        const next = redoRef.current.pop()!;
        void fc.loadFromJSON(JSON.parse(next)).then(() => { fc.renderAll(); syncFromCanvas(fc); });
        setCanUndo(true);
        setCanRedo(redoRef.current.length > 0);
    }, [syncFromCanvas]);

    const handleUndoRef = useRef(handleUndo);
    handleUndoRef.current = handleUndo;
    const handleRedoRef = useRef(handleRedo);
    handleRedoRef.current = handleRedo;

    const persistDecorPosition = useCallback((placementId: number, x: number, y: number) => {
        setDecorPlacements(prev => prev.map(p => p.id === placementId ? { ...p, x, y } : p));
        updateDecorPlacementPosition(placementId, x, y).catch(() => {});
    }, []);
    const updateDecorPlacementPositionRef = useRef(persistDecorPosition);
    updateDecorPlacementPositionRef.current = persistDecorPosition;

    // Items still available to place — capped at quantity (unlimited if not set).
    const placeableDecorItems = decorItems.filter(item => {
        const placedCount = decorPlacements.filter(p => p.decor_id === Number(item.id)).length;
        const cap = item.quantity ?? Infinity;
        return placedCount < cap;
    });

    const handlePlaceDecor = useCallback((item: DecorItem) => {
        const fpId = Number(floorPlan.id);
        if (!fpId) return;
        const x = 40 + (decorPlacements.length % 8) * 90;
        const y = 40 + Math.floor(decorPlacements.length / 8) * 40;
        createDecorPlacement(Number(item.id), fpId, x, y)
            .then(placement => setDecorPlacements(prev => [...prev, placement]))
            .catch(() => {});
        setDecorPickerOpen(false);
    }, [decorPlacements.length, floorPlan.id]);

    const handleRemoveDecorPlacement = useCallback((placementId: number) => {
        deleteDecorPlacement(placementId).catch(() => {});
        setDecorPlacements(prev => prev.filter(p => p.id !== placementId));
        setSelectedDecorPlacementId(null);
    }, []);

    const persistSortOrder = useCallback((fc: Canvas) => {
        const order = new Map(
            fc.getObjects()
                .filter(o => (o as FabricObject & { roomId?: string }).roomId)
                .map((o, i) => [(o as FabricObject & { roomId?: string }).roomId as string, i])
        );
        const rooms = floorPlan.rooms.map(r => order.has(r.id) ? { ...r, sortOrder: order.get(r.id) } : r);
        onFloorPlanUpdate({ ...floorPlan, rooms });
    }, [floorPlan, onFloorPlanUpdate]);

    // ── Top toolbar actions ───────────────────────────────────────────────────
    const clampZoom = (z: number) => Math.min(2.5, Math.max(0.25, z));

    const applyZoom = useCallback((newZoom: number) => {
        const fc = fabricRef.current;
        if (!fc) return;
        const z = clampZoom(newZoom);
        const center = new Point((fc.getWidth() ?? 0) / 2, (fc.getHeight() ?? 0) / 2);
        fc.zoomToPoint(center, z);
        fc.renderAll();
        setZoomPct(Math.round(z * 100));
    }, []);

    const handleZoomOut = useCallback(() => {
        const fc = fabricRef.current;
        if (!fc) return;
        applyZoom(fc.getZoom() - 0.1);
    }, [applyZoom]);

    const handleZoomIn = useCallback(() => {
        const fc = fabricRef.current;
        if (!fc) return;
        applyZoom(fc.getZoom() + 0.1);
    }, [applyZoom]);

    const handleZoomReset = useCallback(() => applyZoom(1), [applyZoom]);

    // Fit the current floor plan's bounding box within the canvas viewport,
    // mirroring the old (pre-Fabric) editor's computeFitZoom() math.
    const handleZoomFit = useCallback(() => {
        const fc = fabricRef.current;
        if (!fc) return;
        const rooms = floorPlanRef.current.rooms;
        if (rooms.length === 0) { applyZoom(1); return; }
        const maxX = Math.max(...rooms.map(r => r.position.x + r.width));
        const maxY = Math.max(...rooms.map(r => r.position.y + r.height));
        const containerW = fc.getWidth() ?? 0;
        const containerH = fc.getHeight() ?? 0;
        const pad = 40;
        const fitZoom = clampZoom(Math.min((containerW - pad) / (maxX || 1), (containerH - pad) / (maxY || 1)));
        fc.setViewportTransform([1, 0, 0, 1, 0, 0]);
        applyZoom(fitZoom);
    }, [applyZoom]);
    handleZoomFitRef.current = handleZoomFit;

    // ── Adding tables, elements and the starting-point preset ────────────────
    // Everything below only edits the open floor plan through onFloorPlanUpdate.
    // Nothing reaches the database until the planner saves, the same as any
    // other edit. New rooms get local (non-uuid) ids, which the save path
    // creates as new zones.
    const newLocalId = useCallback((prefix: string) => `${prefix}-${Date.now()}-${++localIdCounter.current}`, []);

    // Top-left that centres a w x h shape in the part of the plan currently on screen.
    const placementFor = useCallback((w: number, h: number) => {
        const fc = fabricRef.current;
        if (!fc || !fc.viewportTransform) return { x: 100, y: 100 };
        const centre = util.transformPoint(
            new Point((fc.getWidth() ?? 0) / 2, (fc.getHeight() ?? 0) / 2),
            util.invertTransform(fc.viewportTransform),
        );
        const start = { x: Math.max(0, snap(centre.x - w / 2)), y: Math.max(0, snap(centre.y - h / 2)) };
        return cascadePosition(start, floorPlanRef.current.rooms);
    }, []);

    const nextSortOrder = (rooms: Room[]) => rooms.reduce((m, r) => Math.max(m, r.sortOrder ?? 0), -1) + 1;

    const addTableOfShape = useCallback((shape: TableShape) => {
        const fp = floorPlanRef.current;
        const seats = SEAT_LIMITS[shape].default;
        const size = defaultTableSize(shape, seats);
        const room: Room = {
            id: newLocalId('table'),
            name: String(nextTableNumber(fp.rooms.filter(r => r.type === 'table'))),
            type: 'table',
            position: placementFor(size.width, size.height),
            width: size.width,
            height: size.height,
            tableShape: shape,
            seats,
            sortOrder: nextSortOrder(fp.rooms),
        };
        onFloorPlanUpdate({ ...fp, rooms: [...fp.rooms, room] });
    }, [newLocalId, onFloorPlanUpdate, placementFor]);
    addTableRef.current = addTableOfShape;

    const addElementPreset = useCallback((preset: ElementPreset) => {
        const fp = floorPlanRef.current;
        const room: Room = {
            id: newLocalId('el'),
            name: defaultElementName(preset, fp.rooms),
            type: preset.type as Room['type'],
            position: placementFor(preset.width, preset.height),
            width: preset.width,
            height: preset.height,
            sortOrder: nextSortOrder(fp.rooms),
        };
        onFloorPlanUpdate({ ...fp, rooms: [...fp.rooms, room] });
    }, [newLocalId, onFloorPlanUpdate, placementFor]);

    const addWhiteWeddingHall = useCallback(() => {
        const fp = floorPlanRef.current;
        const base = nextSortOrder(fp.rooms);
        const added: Room[] = buildWhiteWeddingHall().map((r, i) => ({
            ...r, id: newLocalId('wwh'), sortOrder: base + i,
        }));
        pendingFitRef.current = true;
        onFloorPlanUpdate({ ...fp, rooms: [...fp.rooms, ...added] });
        showSuccess('White wedding hall added as a starting point (not to scale). Nothing is saved until you save the floor plan.');
    }, [newLocalId, onFloorPlanUpdate, showSuccess]);

    // Quarter turn: swap width and height about the centre. The only rotation
    // that can be saved, since zones have no angle column.
    const rotateRoom = useCallback((roomId: string) => {
        const room = floorPlanRef.current.rooms.find(r => r.id === roomId);
        if (!room || room.locked) return;
        const b = rotateQuarterTurn({ x: room.position.x, y: room.position.y, width: room.width, height: room.height });
        onRoomUpdate(roomId, { position: { x: Math.max(0, snap(b.x)), y: Math.max(0, snap(b.y)) }, width: b.width, height: b.height });
    }, [onRoomUpdate]);

    // Seat count from the toolbar. A rectangular table is resized to fit its
    // chairs (long side = chairs per side), so chairs never crowd or gap.
    const setSeatCount = useCallback((room: Room, seats: number) => {
        const n = clampSeats(room.tableShape, seats);
        if (room.tableShape === 'rectangular') {
            const long = rectangularLength(n);
            const horizontal = room.width >= room.height;
            const c = { x: room.position.x + room.width / 2, y: room.position.y + room.height / 2 };
            const width = horizontal ? long : room.width;
            const height = horizontal ? room.height : long;
            onRoomUpdate(room.id, {
                seats: n, width, height,
                position: { x: Math.max(0, snap(c.x - width / 2)), y: Math.max(0, snap(c.y - height / 2)) },
            });
            return;
        }
        onRoomUpdate(room.id, { seats: n });
    }, [onRoomUpdate]);

    const toggleGrid = useCallback(() => {
        setShowGrid(prev => {
            const next = !prev;
            onFloorPlanUpdate({ ...floorPlanRef.current, showGrid: next });
            return next;
        });
    }, [onFloorPlanUpdate]);

    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc) return;
        applyCanvasBackground(fc, showGrid, !!(floorPlan.imageUrl ?? floorPlan.imageData));
    }, [showGrid, floorPlan.imageUrl, floorPlan.imageData, applyCanvasBackground]);

    const toggleZoomLock = useCallback(() => setZoomLocked(!zoomLockedRef.current), [setZoomLocked]);

    const handleLayerAction = useCallback((action: 'front' | 'forward' | 'backward' | 'back') => {
        const fc = fabricRef.current;
        if (!fc || !selectedRoomId) return;
        const obj = fc.getObjects().find(o => (o as FabricObject & { roomId?: string }).roomId === selectedRoomId);
        if (!obj) return;
        if (action === 'front') fc.bringObjectToFront(obj);
        else if (action === 'forward') fc.bringObjectForward(obj);
        else if (action === 'backward') fc.sendObjectBackwards(obj);
        else fc.sendObjectToBack(obj);
        pinBackgroundToBack(fc);
        reassertBodiesBelowShapes(fc);
        reassertChairsAboveTables(fc);
        reassertDecorationsAboveTables(fc);
        reassertLabelsOnTop(fc);
        fc.renderAll();
        persistSortOrder(fc);
    }, [selectedRoomId, persistSortOrder]);

    // ── Remove room (shared by Delete key and the selection toolbar) ─────────
    const removeRoom = useCallback((roomId: string) => {
        const fc = fabricRef.current;
        if (!fc) return;
        const obj = fc.getObjects().find(o => (o as FabricObject & { roomId?: string }).roomId === roomId);
        if (obj) fc.remove(obj);
        fc.getObjects()
            .filter(o => (o as FabricObject & { labelForRoomId?: string; chairForRoomId?: string; decorationForRoomId?: string })
                .labelForRoomId === roomId
                || (o as FabricObject & { chairForRoomId?: string }).chairForRoomId === roomId
                || (o as FabricObject & { decorationForRoomId?: string }).decorationForRoomId === roomId)
            .forEach(o => fc.remove(o));
        removeBodiesForRoom(fc, roomId);
        fc.renderAll();
        onRoomRemove(roomId);
        setSelectedRoomId(null);
    }, [onRoomRemove]);
    useEffect(() => { removeRoomRef.current = removeRoom; }, [removeRoom]);

    // ── Inline rename commit (migration gap 6/11) ─────────────────────────────
    const commitRename = useCallback((roomId: string) => {
        const val = renameValue.trim();
        if (val) onRoomUpdate(roomId, { name: val });
        setRenamingRoomId(null);
    }, [renameValue, onRoomUpdate]);

    useEffect(() => {
        if (renamingRoomId && renameInputRef.current) {
            renameInputRef.current.focus();
            renameInputRef.current.select();
        }
    }, [renamingRoomId]);

    // ── External selection sync (polish pass) — lets the VenueManager
    // sidebar's zone list select/highlight a room on the Fabric canvas too. ──
    useEffect(() => {
        const fc = fabricRef.current;
        if (!fc || externalSelectedRoomId === undefined) return;
        if (externalSelectedRoomId === null) {
            fc.discardActiveObject();
            fc.renderAll();
            return;
        }
        const obj = fc.getObjects().find(o => (o as FabricObject & { roomId?: string }).roomId === externalSelectedRoomId);
        if (obj && fc.getActiveObject() !== obj) {
            fc.setActiveObject(obj);
            fc.renderAll();
        }
    }, [externalSelectedRoomId]);

    // ── Background image upload ───────────────────────────────────────────────
    const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const objectUrl = URL.createObjectURL(file);
        onFloorPlanUpdate({ ...floorPlan, imageData: objectUrl, imageUrl: undefined });
        uploadImage(file, { entityType: 'floor_plan', entityId: floorPlan.id, label: 'background' })
            .then(att => {
                URL.revokeObjectURL(objectUrl);
                onFloorPlanUpdate({ ...floorPlan, imageUrl: resolveMediaUrl(att.url), imageData: undefined });
                showSuccess('Background saved');
            })
            .catch(() => showError('Upload failed'));
        e.target.value = '';
    }, [floorPlan, onFloorPlanUpdate, showSuccess, showError]);

    // ── SVG import ────────────────────────────────────────────────────────────
    const handleSvgImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => {
            const svgText = ev.target?.result as string;
            const metadata = parseSvgMetadata(svgText);
            if (!metadata) { showError('Not an Anojeje SVG'); return; }
            const zoneRooms: Room[] = (metadata.zones ?? []).map(z => ({
                id:       z.id ?? `imported-${Date.now()}-${Math.random()}`,
                name:     z.name ?? 'Zone',
                type:     (z.type as Room['type']) ?? 'other',
                position: { x: z.x ?? 0, y: z.y ?? 0 },
                width:    z.width ?? 100,
                height:   z.height ?? 80,
                capacity: z.capacity,
                seats: undefined,
                groupId: undefined,
                color: undefined,
                isHeadTable: false,
                namedSeats: [],
            }));
            const tableRooms: Room[] = (metadata.tables ?? []).map(t => ({
                id:       t.id ?? `imported-${Date.now()}-${Math.random()}`,
                name:     t.name ?? 'Table',
                type:     'table',
                position: { x: t.x ?? 0, y: t.y ?? 0 },
                width:    t.width ?? 80,
                height:   t.height ?? 80,
                capacity: t.capacity,
                seats:    t.capacity,
                tableShape: tableShapeFromSvg(t.shape),
                section:  t.section,
                groupId: undefined,
                color: undefined,
                isHeadTable: false,
                namedSeats: [],
            }));
            const rooms = [...zoneRooms, ...tableRooms];
            onFloorPlanUpdate({ ...floorPlan, rooms });
            showSuccess(`Imported ${zoneRooms.length} zones, ${tableRooms.length} tables`);
        };
        reader.readAsText(file);
        e.target.value = '';
    }, [floorPlan, onFloorPlanUpdate, showSuccess, showError]);

    // ── Drag-drop from GuestSeatingPanel ─────────────────────────────────────
    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        const groupId = e.dataTransfer.getData('groupId');
        if (!groupId || !onGroupDrop) return;
        const fc = fabricRef.current;
        if (!fc) return;
        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const target = fc.findTarget(new MouseEvent('mousemove', { clientX: e.clientX, clientY: e.clientY })).target;
        if (target) {
            const rid = (target as FabricObject & { roomId?: string }).roomId;
            if (rid) onGroupDrop(groupId, rid);
        } else {
            // Drop onto background → try to find nearest table by pixel position
            const nearest = floorPlan.rooms
                .filter(r => r.type === 'table')
                .sort((a, b) => {
                    const da = Math.hypot(a.position.x + a.width / 2 - x, a.position.y + a.height / 2 - y);
                    const db = Math.hypot(b.position.x + b.width / 2 - x, b.position.y + b.height / 2 - y);
                    return da - db;
                })[0];
            if (nearest && Math.hypot(nearest.position.x + nearest.width / 2 - x, nearest.position.y + nearest.height / 2 - y) < 80) {
                onGroupDrop(groupId, nearest.id);
            }
        }
    }, [onGroupDrop, floorPlan.rooms]);

    // ── Selection toolbar + inline rename data (migration gap 6/11) ──────────
    const selectedRoom = selectedRoomId ? floorPlan.rooms.find(r => r.id === selectedRoomId) ?? null : null;
    const renamingRoom = renamingRoomId ? floorPlan.rooms.find(r => r.id === renamingRoomId) ?? null : null;

    // ── Top toolbar summary (capacity / type / zone & table counts) ──────────
    const tableCount = floorPlan.rooms.filter(r => r.type === 'table').length;
    const zoneCount = floorPlan.rooms.filter(r => r.type !== 'table').length;

    return (
        <div className="fp-canvas-wrap">
            <div className="fp-toolbar-row">
                <div className="fp-toolbar-summary">
                    <span><strong>{venue.capacity ?? '—'}</strong> Capacity</span>
                    <span className="fp-toolbar-summary-sep" />
                    <span>{floorPlan.name}</span>
                    <span className="fp-toolbar-summary-sep" />
                    <span><strong>{zoneCount}</strong> {zoneCount === 1 ? 'Zone' : 'Zones'}</span>
                    <span className="fp-toolbar-summary-sep" />
                    <span><strong>{tableCount}</strong> {tableCount === 1 ? 'Table' : 'Tables'}</span>
                </div>
                <div className="fp-zoom-toolbar">
                    <div className="fpe-relative-anchor" ref={fpAddRef}>
                        <button
                            className="fp-zoom-btn"
                            title="Add zone, table, or upload background"
                            onClick={() => setFpAddOpen(o => !o)}
                        >
                            <Icon name="plus" size={12} /> Add <Icon name="chevronDown" size={10} />
                        </button>
                        {fpAddOpen && (
                            <div className="header-dropdown-menu fp-add-menu" onMouseLeave={() => setFpAddOpen(false)}>
                                <button className="header-dropdown-item" onClick={() => { setFpAddOpen(false); window.dispatchEvent(new CustomEvent('floor-plan-upload')); }}>
                                    <Icon name="image" size={14} /> Upload background image
                                </button>
                                <div className="header-dropdown-divider" />
                                <div className="header-dropdown-zone-row">
                                    <SearchableSelect
                                        className="header-filter-select"
                                        value={fpZoneType}
                                        onChange={t => {
                                            setFpZoneType(t);
                                            window.dispatchEvent(new CustomEvent('floor-plan-room-type', { detail: t }));
                                            window.dispatchEvent(new CustomEvent('venue-fp-zone-type', { detail: t }));
                                        }}
                                        options={[
                                            { value: 'ceremony', label: 'Ceremony' },
                                            { value: 'reception', label: 'Reception' },
                                            { value: 'cocktail', label: 'Cocktail' },
                                            { value: 'dining', label: 'Dining' },
                                            { value: 'dance', label: 'Dance' },
                                            { value: 'stage', label: 'Stage' },
                                            { value: 'entrance', label: 'Entrance' },
                                            { value: 'restroom', label: 'Restroom' },
                                            { value: 'kitchen', label: 'Kitchen' },
                                            { value: 'other', label: 'Other' },
                                        ]}
                                    />
                                    <button className="header-dropdown-item fpe-flex-1" onClick={() => { setFpAddOpen(false); window.dispatchEvent(new CustomEvent('floor-plan-add-room', { detail: true })); }}>
                                        <Icon name="plus" size={14} /> Add zone
                                    </button>
                                </div>
                                <div className="header-dropdown-divider" />
                                <div className="fp-add-menu-heading">Tables</div>
                                <button className="header-dropdown-item" onClick={() => { setFpAddOpen(false); addTableOfShape('round'); }}>
                                    <Icon name="plus" size={14} /> Round table
                                </button>
                                <button className="header-dropdown-item" title="8 seats along the outer arc, hollow side toward the stage" onClick={() => { setFpAddOpen(false); addTableOfShape('crescent'); }}>
                                    <Icon name="plus" size={14} /> Crescent table
                                </button>
                                <button className="header-dropdown-item" title="8 to 16 seats along both long sides" onClick={() => { setFpAddOpen(false); addTableOfShape('rectangular'); }}>
                                    <Icon name="plus" size={14} /> Long rectangular table
                                </button>
                                <div className="header-dropdown-divider" />
                                <div className="fp-add-menu-heading">Hall elements</div>
                                {ELEMENT_PRESETS.map(preset => (
                                    <button key={preset.type} className="header-dropdown-item" onClick={() => { setFpAddOpen(false); addElementPreset(preset); }}>
                                        <Icon name="plus" size={14} /> {preset.label}
                                    </button>
                                ))}
                                <div className="header-dropdown-divider" />
                                <div className="fp-add-menu-heading">Starting point</div>
                                <button
                                    className="header-dropdown-item"
                                    title="13 crescent tables, 7 long tables, sections A to D, 4 bars, stage and dance floor. Not to scale. Nothing is saved until you save."
                                    onClick={() => { setFpAddOpen(false); addWhiteWeddingHall(); }}
                                >
                                    <Icon name="plus" size={14} /> White wedding hall
                                </button>
                                <p className="fp-add-menu-note">A starting layout to edit, not to scale. It is saved only when you save.</p>
                            </div>
                        )}
                    </div>
                    <div className="fpe-relative-anchor" ref={decorPickerRef}>
                        <button
                            className="fp-zoom-btn"
                            title="Place a procured or planned decor item"
                            onClick={() => setDecorPickerOpen(o => !o)}
                        >
                            ✦ Decor <Icon name="chevronDown" size={10} />
                        </button>
                        {decorPickerOpen && (
                            <div className="header-dropdown-menu fpe-decor-menu">
                                {placeableDecorItems.length === 0 ? (
                                    <div className="header-dropdown-item fpe-decor-empty">
                                        No decor items available to place
                                    </div>
                                ) : placeableDecorItems.map(item => {
                                    const placedCount = decorPlacements.filter(p => p.decor_id === Number(item.id)).length;
                                    const procured = item.status === 'procured' || item.status === 'installed';
                                    return (
                                        <button key={item.id} className="header-dropdown-item fpe-decor-item-btn" onClick={() => handlePlaceDecor(item)}>
                                            <span className="fpe-decor-item-dot" style={{ /* ds-dynamic: procured status color */ color: procured ? '#166534' : '#92400e' }}>✦</span>
                                            <span className="fpe-decor-item-label">{item.decor_element}</span>
                                            {item.quantity != null && (
                                                <span className="fpe-decor-item-count">
                                                    {placedCount}/{item.quantity}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <span className="fp-zoom-divider" />
                    <button className="fp-zoom-btn" title="Undo (Ctrl+Z)" disabled={!canUndo} onClick={handleUndo}>↩</button>
                    <button className="fp-zoom-btn" title="Redo (Ctrl+Y)" disabled={!canRedo} onClick={handleRedo}>↪</button>
                    <span className="fp-zoom-divider" />
                    <button className="fp-zoom-btn" title="Zoom out" onClick={handleZoomOut}>−</button>
                    <button className="fp-zoom-btn fp-zoom-pct" title="Fit to canvas" onClick={handleZoomFit}>Fit</button>
                    <button className="fp-zoom-btn" title="Zoom in" onClick={handleZoomIn}>+</button>
                    <span className="fp-zoom-label">{zoomPct}%</span>
                    <button className="fp-zoom-btn" title="Reset to 100%" onClick={handleZoomReset}>100%</button>
                    <span className="fp-zoom-divider" />
                    <button
                        className={`fp-zoom-btn${showGrid ? ' active' : ''}`}
                        title={showGrid ? 'Hide grid' : 'Show grid'}
                        onClick={toggleGrid}
                    >Grid</button>
                    {/* Calibrate is a canvas tool — you click two points on the
                        plan and give the real-world distance — so it belongs
                        beside Grid and zoom rather than in the breadcrumb bar
                        with Save and Export, which act on the document. */}
                    <button
                        className="fp-zoom-btn"
                        title="Calibrate scale — click two points, then enter the real distance"
                        onClick={() => window.dispatchEvent(new CustomEvent('floor-plan-calibrate'))}
                    ><Icon name="ruler" size={12} /></button>
                    <span className="fp-zoom-divider" />
                    <button
                        className={`fp-zoom-btn${zoomLocked ? ' active' : ''}`}
                        title={zoomLocked ? 'Unlock scroll zoom' : 'Lock scroll zoom'}
                        onClick={toggleZoomLock}
                    ><Icon name={zoomLocked ? 'lock' : 'unlock'} size={12} /></button>
                </div>
            </div>
            <div
                className="floor-canvas embedded fpe-canvas-inner"
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                onDrop={handleDrop}
            >
            <canvas ref={canvasElRef} />
            <input ref={fileInputRef}   type="file" accept="image/*"            onChange={handleImageUpload} className="u-hidden" />
            <input ref={importInputRef} type="file" accept=".svg,image/svg+xml" onChange={handleSvgImport}   className="u-hidden" />
            {selectedRoomId && selectedRoom && fabricRef.current && (() => {
                // Follow the selected object instead of sitting pinned in a
                // fixed corner — a fixed-position toolbar was easy to miss
                // (or think nothing happened) when the selected room was
                // anywhere but the canvas's top-left.
                const fc = fabricRef.current;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _viewportTick = viewportTick; // read so this block re-evaluates on pan
                const cx = selectedRoom.position.x + selectedRoom.width / 2;
                const topY = selectedRoom.position.y;
                const screenPt = util.transformPoint(new Point(cx, topY), fc.viewportTransform!);
                const containerW = fc.getWidth() ?? 0;
                const left = Math.min(Math.max(screenPt.x, 90), Math.max(90, containerW - 90));
                const top = Math.max(screenPt.y - 44, 4);
                return (
                <div className="fp-zoom-toolbar" style={{ /* ds-dynamic: toolbar position follows selected room on canvas */ position: 'absolute', top, left, transform: 'translateX(-50%)' }}>
                    <button className="fp-zoom-btn" title="Send to back" onClick={() => handleLayerAction('back')}>⤓ Back</button>
                    <button className="fp-zoom-btn" title="Send backward" onClick={() => handleLayerAction('backward')}>▼</button>
                    <button className="fp-zoom-btn" title="Bring forward" onClick={() => handleLayerAction('forward')}>▲</button>
                    <button className="fp-zoom-btn" title="Bring to front" onClick={() => handleLayerAction('front')}>⤒ Front</button>
                    <span className="fp-zoom-divider" />
                    <button
                        className="fp-zoom-btn"
                        title="Rename"
                        disabled={selectedRoom.locked}
                        onClick={() => { setRenamingRoomId(selectedRoom.id); setRenameValue(selectedRoom.name); }}
                    ><Icon name="edit" size={12} /></button>
                    {selectedRoom.type === 'table' && (
                        <label className="fp-zoom-btn fpe-color-label" title="Table colour">
                            <span
                                className="fpe-color-swatch"
                                style={{ /* ds-dynamic: table color from custom/group color */ background: selectedRoom.customColor ?? selectedRoom.groupColor ?? '#8b5a3c' }}
                            />
                            <input
                                type="color"
                                value={selectedRoom.customColor ?? selectedRoom.groupColor ?? '#8b5a3c'}
                                onChange={e => onRoomUpdate(selectedRoom.id, { customColor: e.target.value })}
                                className="fpe-color-input-overlay"
                            />
                        </label>
                    )}
                    {selectedRoom.type === 'table' && (
                        <SeatCountInput
                            shape={selectedRoom.tableShape}
                            value={selectedRoom.seats ?? selectedRoom.capacity}
                            disabled={selectedRoom.locked}
                            onCommit={n => setSeatCount(selectedRoom, n)}
                        />
                    )}
                    <button
                        className="fp-zoom-btn"
                        title="Rotate a quarter turn"
                        disabled={selectedRoom.locked}
                        onClick={() => rotateRoom(selectedRoom.id)}
                    ><Icon name="refresh" size={12} /></button>
                    {selectedRoom.type === 'table' && (
                        <button
                            className={`fp-zoom-btn${selectedRoom.isHeadTable ? ' active' : ''}`}
                            title={selectedRoom.isHeadTable ? 'Head table (click to unmark)' : 'Mark as head table (enables named seats)'}
                            disabled={selectedRoom.locked}
                            onClick={() => onRoomUpdate(selectedRoom.id, { isHeadTable: !selectedRoom.isHeadTable })}
                        ><Icon name="star" size={12} /></button>
                    )}
                    <button
                        className={`fp-zoom-btn${selectedRoom.locked ? ' active' : ''}`}
                        title={selectedRoom.locked ? 'Unlock (allow drag/resize)' : 'Lock (prevent drag/resize)'}
                        onClick={() => onRoomUpdate(selectedRoom.id, { locked: !selectedRoom.locked })}
                    ><Icon name={selectedRoom.locked ? 'unlock' : 'lock'} size={12} /></button>
                    <button className="fp-zoom-btn" title="Delete" onClick={() => removeRoom(selectedRoom.id)}>✕</button>
                </div>
                );
            })()}
            {/* Named seats — head table only. A separate panel below the main
                toolbar rather than crammed into it: the toolbar is a fixed
                row of icon buttons, but this needs one row per seat and
                grows with capacity. */}
            {selectedRoomId && selectedRoom && selectedRoom.type === 'table' && selectedRoom.isHeadTable && fabricRef.current && (() => {
                const fc = fabricRef.current;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _viewportTick = viewportTick;
                const cx = selectedRoom.position.x + selectedRoom.width / 2;
                const bottomY = selectedRoom.position.y + selectedRoom.height;
                const screenPt = util.transformPoint(new Point(cx, bottomY), fc.viewportTransform!);
                const containerW = fc.getWidth() ?? 0;
                const left = Math.min(Math.max(screenPt.x, 110), Math.max(110, containerW - 110));
                const top = screenPt.y + 8;
                const capacity = selectedRoom.seats ?? selectedRoom.capacity ?? 0;
                const namedSeats = selectedRoom.namedSeats ?? [];
                const setSeatName = (seatIndex: number, guestName: string) => {
                    const next = namedSeats.filter(s => s.seatIndex !== seatIndex);
                    if (guestName.trim()) next.push({ seatIndex, guestId: '', guestName });
                    next.sort((a, b) => a.seatIndex - b.seatIndex);
                    onRoomUpdate(selectedRoom.id, { namedSeats: next });
                };
                return (
                    <div className="fp-named-seats-panel" style={{ /* ds-dynamic: panel position follows selected table on canvas */ position: 'absolute', top, left, transform: 'translateX(-50%)' }}>
                        <div className="fp-named-seats-header">Named seats</div>
                        {capacity === 0 ? (
                            <p className="fp-named-seats-empty">Set a seat count first.</p>
                        ) : (
                            Array.from({ length: capacity }, (_, i) => i + 1).map(seatIndex => (
                                <input
                                    key={seatIndex}
                                    type="text"
                                    className="fp-named-seats-input"
                                    placeholder={`Seat ${seatIndex}`}
                                    disabled={selectedRoom.locked}
                                    value={namedSeats.find(s => s.seatIndex === seatIndex)?.guestName ?? ''}
                                    onChange={e => setSeatName(seatIndex, e.target.value)}
                                />
                            ))
                        )}
                    </div>
                );
            })()}
            {selectedDecorPlacementId !== null && fabricRef.current && (() => {
                const placement = decorPlacements.find(p => p.id === selectedDecorPlacementId);
                if (!placement) return null;
                const fc = fabricRef.current;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _viewportTick = viewportTick;
                const screenPt = util.transformPoint(new Point(placement.x, placement.y), fc.viewportTransform!);
                const containerW = fc.getWidth() ?? 0;
                const left = Math.min(Math.max(screenPt.x, 60), Math.max(60, containerW - 60));
                const top = Math.max(screenPt.y - 34, 4);
                return (
                <div className="fp-zoom-toolbar" style={{ /* ds-dynamic: toolbar position follows selected decor placement on canvas */ position: 'absolute', top, left, transform: 'translateX(-50%)' }}>
                    <button className="fp-zoom-btn" title="Remove this placement" onClick={() => handleRemoveDecorPlacement(placement.id)}>✕ Remove</button>
                </div>
                );
            })()}
            {renamingRoom && fabricRef.current && (() => {
                const fc = fabricRef.current!;
                const zoom = fc.getZoom() ?? 1;
                const cx = renamingRoom.position.x + renamingRoom.width / 2;
                const cy = renamingRoom.position.y + renamingRoom.height / 2;
                const screenPt = util.transformPoint(new Point(cx, cy), fc.viewportTransform!);
                return (
                    <input
                        ref={renameInputRef}
                        className="fp-inline-rename"
                        style={{ /* ds-dynamic: inline rename input position/size follows room on canvas */
                            position: 'absolute', left: screenPt.x, top: screenPt.y,
                            transform: 'translate(-50%, -50%)',
                            width: Math.max(60, renamingRoom.width * zoom * 0.8),
                        }}
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onBlur={() => commitRename(renamingRoom.id)}
                        onKeyDown={e => {
                            e.stopPropagation();
                            if (e.key === 'Enter') { e.preventDefault(); commitRename(renamingRoom.id); }
                            if (e.key === 'Escape') { e.preventDefault(); setRenamingRoomId(null); }
                        }}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                    />
                );
            })()}
            {/* Scale calibration overlay */}
            {calibrating && (
                <div className="fp-calib-hint">
                    {calibPt1 ? 'Click second point' : 'Click first point'}
                    <button className="fp-calib-cancel" onClick={e => {
                        e.stopPropagation();
                        window.dispatchEvent(new CustomEvent('floor-plan-calibrate'));
                    }}>Cancel</button>
                </div>
            )}
            {calibrating && calibPt1 && fabricRef.current && (() => {
                const screenPt = util.transformPoint(
                    new Point(calibPt1.x, calibPt1.y),
                    fabricRef.current.viewportTransform!,
                );
                return <div className="fp-calib-dot" style={{ /* ds-dynamic: calibration dot position on canvas */ left: screenPt.x, top: screenPt.y }} />;
            })()}

            {/* Scale bar */}
            {floorPlan.pixelsPerMeter && fabricRef.current && (
                <div className="fp-scale-bar">
                    <div className="fp-scale-bar-line" style={{ /* ds-dynamic: scale bar width from pixels-per-meter and zoom */
                        width: Math.round(floorPlan.pixelsPerMeter * (fabricRef.current.getZoom() ?? 1) * 5),
                    }} />
                    <span className="fp-scale-bar-label">5 m</span>
                </div>
            )}

            {/* Calibration distance dialog */}
            {calibDialogOpen && (
                <div className="fp-calib-dialog" onClick={e => e.stopPropagation()}>
                    <div className="fp-calib-dialog-inner">
                        <div className="fp-calib-dialog-title">Set real-world distance</div>
                        <div className="fp-calib-dialog-body">
                            <label>Distance between the two points (metres)</label>
                            <input
                                type="number"
                                min="0.1"
                                step="0.5"
                                value={calibDistance}
                                onChange={e => setCalibDistance(e.target.value)}
                                placeholder="e.g. 10"
                                autoFocus
                            />
                        </div>
                        <div className="fp-calib-dialog-actions">
                            <button onClick={() => {
                                const d = parseFloat(calibDistance);
                                if (!d || !calibPt1 || !calibPt2) return;
                                const dx = calibPt2.x - calibPt1.x;
                                const dy = calibPt2.y - calibPt1.y;
                                const pixDist = Math.sqrt(dx * dx + dy * dy);
                                const ppm = pixDist / d;
                                onFloorPlanUpdate({ ...floorPlan, pixelsPerMeter: ppm });
                                setCalibDialogOpen(false);
                                setCalibDistance('');
                                showSuccess(`Scale set: ${ppm.toFixed(1)} px/m`);
                                window.dispatchEvent(new CustomEvent('floor-plan-calibrate'));
                            }}>Set Scale</button>
                            <button onClick={() => {
                                setCalibDialogOpen(false);
                                window.dispatchEvent(new CustomEvent('floor-plan-calibrate'));
                            }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default FloorPlanEditorFabric;
